/**
 * Titlestrove Landing Page - Interactive Features
 * TypeScript implementation for form handling, scroll animations, and interactions
 */

// ============================================
// Type Definitions
// ============================================

interface ScrollObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}

// ============================================
// DOM Elements
// ============================================

const notifyForm = document.getElementById("notifyForm") as HTMLFormElement;
const notifyInput = document.querySelector(".notify-input") as HTMLInputElement;
const successMessage = document.getElementById("successMessage") as HTMLElement;
const featureCards = document.querySelectorAll(".feature-card");
const platformItems = document.querySelectorAll(".platform-item");

// ============================================
// Form Handling
// ============================================

/**
 * Handle email notification form submission
 */
function handleFormSubmit(event: Event): void {
  event.preventDefault();

  const email = notifyInput.value.trim();

  if (!isValidEmail(email)) {
    showError("Please enter a valid email address");
    return;
  }

  // Simulate API call
  simulateApiCall(email)
    .then(() => {
      showSuccess();
      notifyForm.reset();
    })
    .catch((error) => {
      showError("Something went wrong. Please try again.");
      console.error("Form submission error:", error);
    });
}

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Simulate API call for email subscription
 */
function simulateApiCall(email: string): Promise<void> {
  return new Promise((resolve) => {
    // Simulate network delay
    setTimeout(() => {
      // In a real application, this would be an actual API call
      console.log("Email submitted:", email);
      resolve();
    }, 1000);
  });
}

/**
 * Show success message
 */
function showSuccess(): void {
  successMessage.classList.add("show");
  notifyForm.style.display = "none";

  // Hide success message after 5 seconds
  setTimeout(() => {
    successMessage.classList.remove("show");
    notifyForm.style.display = "flex";
  }, 5000);
}

/**
 * Show error message
 */
function showError(message: string): void {
  // Create temporary error message
  const errorDiv = document.createElement("div");
  errorDiv.className = "error-message";
  errorDiv.textContent = message;
  errorDiv.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 1rem;
        background: rgba(244, 63, 94, 0.1);
        border: 1px solid rgba(244, 63, 94, 0.3);
        border-radius: 0.75rem;
        color: #f43f5e;
        font-weight: 500;
        margin-top: 1rem;
        animation: fadeIn 0.3s ease;
    `;

  notifyForm.insertAdjacentElement("afterend", errorDiv);

  // Remove error message after 3 seconds
  setTimeout(() => {
    errorDiv.style.opacity = "0";
    errorDiv.style.transition = "opacity 0.3s ease";
    setTimeout(() => errorDiv.remove(), 300);
  }, 3000);
}

// ============================================
// Scroll Animations
// ============================================

/**
 * Create intersection observer for scroll animations
 */
function createScrollObserver(
  options: ScrollObserverOptions = {}
): IntersectionObserver {
  const defaultOptions: ScrollObserverOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
  };

  const mergedOptions = { ...defaultOptions, ...options };

  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("visible");
        // Optional: Unobserve after animation
        // observer.unobserve(entry.target);
      }
    });
  }, mergedOptions);
}

/**
 * Initialize scroll animations
 */
function initScrollAnimations(): void {
  const observer = createScrollObserver();

  // Observe feature cards with staggered delay
  featureCards.forEach((card, index) => {
    (card as HTMLElement).style.transitionDelay = `${index * 0.1}s`;
    observer.observe(card);
  });

  // Observe platform items
  platformItems.forEach((item, index) => {
    (item as HTMLElement).style.transitionDelay = `${index * 0.15}s`;
    observer.observe(item);
  });
}

// ============================================
// Smooth Scroll
// ============================================

/**
 * Handle smooth scroll for navigation links
 */
function initSmoothScroll(): void {
  const navLinks = document.querySelectorAll(".nav-link, .footer-link");

  navLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = (link as HTMLAnchorElement).getAttribute("href");

      if (href && href.startsWith("#")) {
        e.preventDefault();
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          const offsetTop = targetElement.offsetTop - 80; // Account for fixed nav
          window.scrollTo({
            top: offsetTop,
            behavior: "smooth",
          });
        }
      }
    });
  });
}

// ============================================
// 3D Block Game Controller
// ============================================

// Import block library
import {
  BlockEngine,
  type Vec3,
  type Vec4,
  type Block,
} from "./lib/block/index";

/**
 * Generate the Controller Model using Block definitions
 * @returns Array of block definitions
 */
function generateControllerBlocks(): Block[] {
  const blocks: Block[] = [];
  const add = (
    center: Vec3,
    width: number,
    height: number,
    depth: number,
    color: Vec4
  ) => {
    blocks.push({ center, width, height, depth, color });
  };

  const colors: Record<string, Vec4> = {
    body: [0.85, 0.85, 0.9, 1.0],
    dpad: [0.2, 0.2, 0.25, 1.0],
    select: [0.2, 0.2, 0.25, 1.0],
    red: [0.9, 0.2, 0.2, 1.0],
    yellow: [0.95, 0.8, 0.1, 1.0],
    blue: [0.2, 0.3, 0.9, 1.0],
    green: [0.1, 0.7, 0.2, 1.0],
    shoulder: [0.7, 0.7, 0.75, 1.0],
  };

  // 1. Main Body - Left Side
  add([-6.5, 0, 0], 1, 5, 2, colors.body);

  // 2. Main Body - Right Side
  add([6.5, 0, 0], 1, 5, 2, colors.body);

  // 3. Main Body - Center Section
  add([0, 0, 0], 13, 6, 2, colors.body);

  // 4. D-Pad (cross shape)
  add([-3.5, 0.5, 1], 3, 1, 1, colors.dpad); // Horizontal
  add([-3.5, 0.5, 1], 1, 3, 1, colors.dpad); // Vertical

  // 5. Buttons
  add([5, 0.5, 1], 1, 1, 1, colors.red); // Right
  add([3.5, -1, 1], 1, 1, 1, colors.yellow); // Bottom
  add([3.5, 2, 1], 1, 1, 1, colors.blue); // Top
  add([2, 0.5, 1], 1, 1, 1, colors.green); // Left

  // 6. Select/Start Buttons
  add([-1, -1, 1], 1, 0.5, 1, colors.select);
  add([1, -1, 1], 1, 0.5, 1, colors.select);

  // 7. Shoulder Buttons
  add([-3.5, 3, 0], 5, 0.5, 1, colors.shoulder);
  add([3.5, 3, 0], 5, 0.5, 1, colors.shoulder);

  return blocks;
}

/**
 * Initialize 3D block game controller using block definitions
 */
function init3DController(): void {
  const blocks = generateControllerBlocks();
  try {
    const engine = new BlockEngine("controllerCanvas", blocks, 1);
    engine.start();
  } catch (e) {
    console.error("Failed to init 3D controller", e);
  }
}

// ============================================
// Typing Effect
// ============================================

/**
 * Add typing effect to hero badge (optional enhancement)
 */
function initTypingEffect(): void {
  const badgeText = document.querySelector(
    ".hero-badge span:last-child"
  ) as HTMLElement;

  if (!badgeText) return;

  const text = badgeText.textContent || "";
  badgeText.textContent = "";

  let index = 0;
  const typingSpeed = 100;

  function type(): void {
    if (index < text.length) {
      badgeText.textContent += text.charAt(index);
      index++;
      setTimeout(type, typingSpeed);
    }
  }

  // Start typing after initial animations
  setTimeout(type, 1500);
}

// ============================================
// Navigation Scroll Effect
// ============================================

/**
 * Add scroll effect to navigation bar
 */
function initNavScroll(): void {
  const nav = document.querySelector(".nav") as HTMLElement;

  if (!nav) return;

  window.addEventListener("scroll", () => {
    const currentScroll = window.pageYOffset;

    if (currentScroll > 100) {
      nav.style.background = "rgba(10, 10, 11, 0.95)";
      nav.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.3)";
    } else {
      nav.style.background = "rgba(10, 10, 11, 0.8)";
      nav.style.boxShadow = "none";
    }
  });
}

// ============================================
// Initialize Everything
// ============================================

/**
 * Initialize all interactive features when DOM is ready
 */
function init(): void {
  // Form handling
  if (notifyForm) {
    notifyForm.addEventListener("submit", handleFormSubmit);
  }

  // Scroll animations
  initScrollAnimations();

  // Smooth scroll
  initSmoothScroll();

  // Mouse move effect
  init3DController();

  // Typing effect
  initTypingEffect();

  // Navigation scroll effect
  initNavScroll();

  console.log("Titlestrove landing page initialized");
}

// ============================================
// Start Application
// ============================================

// Wait for DOM to be fully loaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
